import ExpandingTable from './ExpandingConfigTable';
import { CheckOutlined, CloseOutlined, DownOutlined, MinusOutlined, PlayCircleOutlined, UpOutlined } from '@ant-design/icons';
import {
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Drawer,
  Fade,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  Menu,
  MenuItem,
  Modal,
  OutlinedInput,
  Stack,
  Switch,
  Tooltip,
  Typography
} from '@mui/material';
import IconButton from '../../components/@extended/IconButton';
import { alpha, styled, useTheme } from '@mui/material/styles';
import { useAccountClient, useTradeClient } from '../../client/client-hook';
import React, { useEffect, useMemo, useState } from 'react';
import useAuth from '../../hooks/useAuth';
import useLocalStorage from '../../hooks/useLocalStorage';
import {
  DeleteConfigReq,
  DeleteSalvesReq,
  GetUserConfigsReply,
  GetUserConfigsReq,
  PauseAccountReq,
  Slave,
  UpdateConfigReq,
  UpdateSlaveReq
} from '../../proto/trade-copier_pb';
import Loader from '../../components/Loader';
import { DispatchSnackbarService } from '../../services';
import { useDispatch } from '../../store';
import Backdrop from '@mui/material/Backdrop';
import AddSlaveForm from './components/AddSlaveForm';
import MainCard from '../../components/MainCard';
import { Crisp } from 'crisp-sdk-web';
import LoadingButton from '../../components/@extended/LoadingButton';
import CustomConfigSnackbar from './CustomConfigSnackbar';
import { cloneDeep } from 'lodash';
import { mt4Options, mt5Options } from './AddCopierSection';
import { OpenTradeWarningReply, OpenTradeWarningReq } from 'proto/account-manager_pb';
import ConfirmCloseDialog from 'components/custom/ConfirmCloseDialog';
import TableWrapper from '../../components/@extended/table-wrapper';
import CopierEditDialog from './CopierEditDialog';
import CopierOverrideDialog from './CopierOverrideDialog';
import CopierAddTradingHoursDialog from './CopierAddTradingHoursDialog';
import CopierBulkEditDialog from './CopierBulkEditDialog';
import { sizeByBreakpoint, textSizeByBreakpoint } from '../../utils/helper';

import { ReactComponent as PlusIcon } from '../../assets/images/icons/add.svg';
import { ReactComponent as ArrowIcon } from '../../assets/images/icons/arrow-forward.svg';
import { ReactComponent as ClockIcon } from '../../assets/images/icons/clock.svg';
import { ReactComponent as PauseIcon } from '../../assets/images/icons/pause.svg';
import { ReactComponent as TrashIcon } from '../../assets/images/icons/trash6.svg';
import { ReactComponent as EditIcon } from '../../assets/images/icons/edit2.svg';
import { ReactComponent as DirectionIcon } from '../../assets/images/icons/direction.svg';
import { useNavigate } from 'react-router-dom';

interface ThreeStateToggleProps {
  value: 'active' | 'paused' | 'disabled';
  onChange?: (newValue: 'active' | 'paused' | 'disabled') => void;
  disabled?: boolean;
}

const ThreeStateToggle = ({ value, onChange, disabled = false }: ThreeStateToggleProps) => {
  const theme = useTheme();

  const getBackgroundColor = () => {
    if (disabled) {
      return theme.palette.grey[400];
    }
    switch (value) {
      case 'active':
        return theme.palette.success.main;
      case 'paused':
        return theme.palette.warning.main;
      case 'disabled':
        return theme.palette.grey[400];
      default:
        return theme.palette.grey[400];
    }
  };

  const getIcon = () => {
    switch (value) {
      case 'active':
        return <CheckOutlined style={{ fontSize: '13px', fontWeight: 'bold', strokeWidth: '50px' }} />;
      case 'paused':
        return <MinusOutlined style={{ fontSize: '10px' }} />;
      case 'disabled':
        return <CloseOutlined style={{ fontSize: '8px' }} />;
      default:
        return <CloseOutlined style={{ fontSize: '8px' }} />;
    }
  };

  const getThumbPosition = () => {
    switch (value) {
      case 'disabled':
        return '2px'; // Left position
      case 'paused':
        return '21px'; // Middle position (60 - 18 - 2px padding on each side) / 2 + 2px = 21px
      case 'active':
        return '40px'; // Right position (60 - 18 - 2px = 40px)
      default:
        return '2px';
    }
  };

  const getAriaLabel = () => {
    switch (value) {
      case 'active':
        return 'Status: Active. Press Space or Enter to cycle, Left Arrow for Paused, Right Arrow stays Active';
      case 'paused':
        return 'Status: Paused. Press Space or Enter to cycle, Left Arrow for Disabled, Right Arrow for Active';
      case 'disabled':
        return 'Status: Disabled. Press Space or Enter to cycle, Left Arrow stays Disabled, Right Arrow for Paused';
      default:
        return 'Status toggle';
    }
  };

  const cycleToNextState = () => {
    if (disabled || !onChange) return;
    // Cycle: disabled -> paused -> active -> disabled
    const nextState: Record<'active' | 'paused' | 'disabled', 'active' | 'paused' | 'disabled'> = {
      disabled: 'paused',
      paused: 'active',
      active: 'disabled'
    };
    onChange(nextState[value]);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (disabled || !onChange) return;

    switch (e.key) {
      case ' ':
      case 'Enter':
        e.preventDefault();
        cycleToNextState();
        break;
      case 'ArrowRight':
      case 'ArrowUp':
        e.preventDefault();
        // Move towards 'active' (right)
        if (value === 'disabled') onChange('paused');
        else if (value === 'paused') onChange('active');
        break;
      case 'ArrowLeft':
      case 'ArrowDown':
        e.preventDefault();
        // Move towards 'disabled' (left)
        if (value === 'active') onChange('paused');
        else if (value === 'paused') onChange('disabled');
        break;
    }
  };

  const handleClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || !onChange) return;

    // Get the click position relative to the toggle
    const rect = e.currentTarget.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const toggleWidth = rect.width;

    // Determine which third was clicked
    const leftThird = toggleWidth / 3;
    const rightThird = (toggleWidth / 3) * 2;

    let newState: 'active' | 'paused' | 'disabled';
    if (clickX < leftThird) {
      newState = 'disabled';
    } else if (clickX < rightThird) {
      newState = 'paused';
    } else {
      newState = 'active';
    }

    onChange(newState);
  };

  return (
    <Box
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      tabIndex={disabled ? -1 : 0}
      role="slider"
      aria-valuemin={0}
      aria-valuemax={2}
      aria-valuenow={value === 'disabled' ? 0 : value === 'paused' ? 1 : 2}
      aria-valuetext={value}
      aria-label={getAriaLabel()}
      aria-disabled={disabled}
      sx={{
        width: 60,
        height: 22,
        borderRadius: '11px',
        backgroundColor: getBackgroundColor(),
        position: 'relative',
        cursor: disabled ? 'default' : 'pointer',
        transition: 'background-color 0.3s ease',
        opacity: disabled ? 0.6 : 1,
        '&:hover': {
          opacity: disabled ? 0.6 : 0.9
        },
        '&:focus': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px'
        },
        '&:focus:not(:focus-visible)': {
          outline: 'none'
        },
        '&:focus-visible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: '2px'
        }
      }}
    >
      <Box
        sx={{
          width: 18,
          height: 18,
          borderRadius: '50%',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
          position: 'absolute',
          top: '2px',
          left: getThumbPosition(),
          transition: 'left 0.3s ease'
        }}
      >
        <Box sx={{ color: getBackgroundColor(), display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{getIcon()}</Box>
      </Box>
    </Box>
  );
};

export const Android12Switch = styled(Switch)(({ theme }) => ({
  '& .MuiSwitch-track': {
    borderRadius: 22 / 2,
    '&:before, &:after': {
      content: '""',
      position: 'absolute',
      top: '50%',
      transform: 'translateY(-50%)',
      width: 16,
      height: 16
    },
    '&:before': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M21,7L9,19L3.5,13.5L4.91,12.09L9,16.17L19.59,5.59L21,7Z"/></svg>')`,
      left: 4
    },
    '&:after': {
      backgroundImage: `url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" height="16" width="16" viewBox="0 0 24 24"><path fill="${encodeURIComponent(
        theme.palette.getContrastText(theme.palette.primary.main)
      )}" d="M19,13H5V11H19V13Z" /></svg>')`,
      right: 4
    }
  }
}));
Android12Switch.displayName = 'Android12Switch';

const styleSymbolModal = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  minWidth: 720,
  maxWidth: 800,
  bgcolor: 'background.paper',
  borderRadius: '8px'
  // boxShadow: 24,
  // p: 4
};

const service = new DispatchSnackbarService();

export type CopyTradingTableDto = {
  accountName: string;
  accountId: string;
  riskType: string;
  copyFrom: string;
  copyFromId: string;
  riskSetting?: string;
  status: 'left' | 'right' | 'center';
  children?: CopyTradingTableDto[];
  configId: string;
  isMaster: boolean;
  id: string;
  actualAccountId: string;
  additionalProps?: any;
  expanded: boolean;
  symbolMismatch?: boolean;
  pauseTimeSec?: number;
  slaveData?: Slave.AsObject;
  updatedValues?: Slave.AsObject;
  customCommentType?: 'Yes' | 'No' | 'Custom';
  copyComment?: string;
  copyMagic?: string;
  customMagicType?: 'Yes' | 'No' | 'Custom';
  delayType?: 'null' | 'no' | 'fixed' | 'random';
  selected: boolean;
  parentId?: string;
  placedTypeOptions:
    | {
        name: string;
        value: string;
      }[]
    | null;
  masterAccountNumber?: string;
  masterPlatform?: string;
};

export const risk_type_map: any = {
  FIXED_LOT: 'Fixed Lot',
  LOT_MULTIPLIER: 'Lot Multiplier',
  BALANCE_MULTIPLIER: 'Balance Multiplier',
  FIXED_BALANCE_MULTIPLIER: 'Fixed Balance Multiplier',
  BALANCE_PERCENTAGE: 'Fixed % risk'
};

export const risk_type_display_map: any = {
  1: 'Fixed Lot',
  2: 'Lot Multiplier',
  3: 'Balance Multiplier',
  4: 'Fixed Balance Multiplier',
  5: 'Fixed % risk'
};

export const risk_type_map_value: any = {
  UNKNOWN: 0,
  FIXED_LOT: 1,
  LOT_MULTIPLIER: 2,
  BALANCE_MULTIPLIER: 3,
  FIXED_BALANCE_MULTIPLIER: 4,
  BALANCE_PERCENTAGE: 5
};

export const filter_type_map_value: any = {
  UNKNOWN: 0,
  COPY: 1,
  IGNORE: 2
};

export const filter_type_map_key: any = {
  0: 'UNKNOWN',
  1: 'COPY',
  2: 'IGNORE'
};

type Prop = {
  reloadRequired: boolean;
};

const CopyTradingTableSection = ({ reloadRequired }: Prop) => {
  // const theme = useTheme();
  const tradeClient = useTradeClient();
  const { user } = useAuth();
  const [configList, setConfigList] = useState<CopyTradingTableDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedMasters, setExpandedMasters] = useLocalStorage<Record<string, boolean>>('copier-expanded-state', {});
  const [selectedRow, setSelectedRow] = useState<CopyTradingTableDto | null>(null);
  const [selectedSlaves, setSelectedSlaves] = useState<CopyTradingTableDto[] | null>(null);
  const [selectedMaster, setSelectedMaster] = useState<CopyTradingTableDto | null>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedMasterAccIDs, setSelectedMasterAccIDs] = useState<{
    accountID: string;
    actualAccountID: string;
  } | null>(null);
  const [showWarning, setShowWarning] = useState<boolean>(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [checkedVal, setCheckedVal] = useState<'left' | 'right' | 'center' | null>(null);
  const [warnType, setWarnType] = useState<'disable' | 'delete' | null>(null);
  const [slaveWarn, setSlaveWarn] = useState<boolean>(false);
  const dispatch = useDispatch();
  const theme = useTheme();
  const accountClient = useAccountClient();
  const navigate = useNavigate();

  const [addSlaveModalOpen, setAddSlaveModalOpen] = useState(false);
  const [editSlaveModalOpen, setEditSlaveModalOpen] = useState(false);
  const [editTradingHour, setEditTradingHour] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [editSlavesModalOpen, setEditSlavesModalOpen] = useState(false);

  const [addSlaveFormState, setAddSlaveFormState] = useState(0);

  useEffect(() => {
    if (reloadRequired && user && user?.tcId) {
      setLoading(true);
      fetchConfigs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadRequired]);

  useEffect(() => {
    if (user && user?.tcId) {
      setLoading(true);
      fetchConfigs();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const [listMasters, setListMasters] = useState<CopyTradingTableDto[]>([]);

  const fetchConfigs = () => {
    tradeClient()
      .then((client) => {
        const req: GetUserConfigsReq.AsObject = {
          userId: user?.tcId || ''
        };
        client
          .getUserConfigs(req)
          .then((res: any) => {
            const result = res as GetUserConfigsReply.AsObject;
            if (result?.configsList) {
              const configs = result.configsList.map((config) => {
                let status: 'left' | 'right' | 'center' = 'left';
                if (!config?.enabled && !config.manageExisting) {
                  status = 'left';
                } else if (config?.enabled && !config.manageExisting) {
                  status = 'right';
                } else if (!config?.enabled && config.manageExisting) {
                  status = 'center';
                }
                let masterPauseUntil = 0;
                for (const slave of config.slavesList) {
                  if (slave.pauseUntil && slave.pauseUntil?.seconds * 1000 > new Date().getTime()) {
                    masterPauseUntil = slave.pauseUntil?.seconds;
                  } else {
                    masterPauseUntil = 0;
                    break;
                  }
                }
                const tmp: CopyTradingTableDto = {
                  id: config?.master?.id || '',
                  configId: config?.id || '',
                  isMaster: true,
                  accountName: config?.master?.nickname || '',
                  accountId: config?.master?.accountNumber?.toString() || '',
                  status: status,
                  copyFrom: ' - ',
                  copyFromId: ' - ',
                  riskType: ' - ',
                  riskSetting: ' - ',
                  actualAccountId: config?.master?.accountId || '',
                  expanded: expandedMasters[config?.master?.accountId || ''] ?? true,
                  pauseTimeSec: masterPauseUntil,
                  selected: false,
                  placedTypeOptions: null,
                  children: config.slavesList.map((x) => {
                    let tmpStatus: 'left' | 'right' | 'center' = 'left';
                    if (!x.enabled?.value && !x?.manageExisting?.value) {
                      tmpStatus = 'left';
                    } else if (x.enabled?.value && !x?.manageExisting?.value) {
                      tmpStatus = 'right';
                    } else if (!x.enabled?.value && x?.manageExisting?.value) {
                      tmpStatus = 'center';
                    }
                    // For CTR accounts, nickname is like "Account 1 (4123957)" - transform to "Account 1 - 4123957"
                    // For other platforms, append account number like "FBS MT5 - 102201802"
                    let slaveAccountName = x.nickname;
                    if (x.platform === 'CTR') {
                      slaveAccountName = x.nickname.replace(/\s*\((\d+)\)\s*$/, ' - $1');
                    } else if (x.accountNumber) {
                      slaveAccountName = x.nickname + ` - ${x.accountNumber}`;
                    }
                    const tmpSlave: CopyTradingTableDto = {
                      id: x.id,
                      parentId: config?.master?.id || '',
                      configId: config.id,
                      isMaster: false,
                      accountName: slaveAccountName,
                      copyFrom: config?.master?.accountNumber?.toString() || '',
                      copyFromId: config?.master?.accountId || '',
                      status: tmpStatus || false,
                      riskType: x.riskType?.value ? risk_type_map[x.riskType?.value] : '',
                      riskSetting:
                        x.riskType?.value === 'FIXED_LOT'
                          ? x.fixedLotValue?.value?.toString() + ' Lot'
                          : x.riskType?.value === 'LOT_MULTIPLIER'
                          ? x.lotMultiplierValue?.value?.toString() + '%'
                          : x.riskType?.value === 'BALANCE_PERCENTAGE'
                          ? x.balancePercentageValue?.value?.toString() + '%'
                          : x.riskType?.value === 'BALANCE_MULTIPLIER'
                          ? x.balanceMultiplierValue?.value?.toString() + '%'
                          : x.riskType?.value === 'FIXED_BALANCE_MULTIPLIER'
                          ? '$' + x.fixedMasterBalance?.value?.toString() + ' | ' + x.fixedBalanceMultiplierValue?.value?.toString() + '%'
                          : ' - ',
                      accountId: x.accountNumber?.toString(),
                      actualAccountId: x.accountId,
                      symbolMismatch: x.showSymbolMismatch?.value && x.symbolMismatch?.value,
                      pauseTimeSec: x.pauseUntil?.seconds || 0,
                      selected: false,
                      placedTypeOptions: x.platform === 'MT-4' ? mt4Options : x.platform === 'MT-5' ? mt5Options : [],
                      masterAccountNumber: config?.master?.accountNumber?.toString() || '',
                      masterPlatform: config?.master?.platform || '',
                      additionalProps: {
                        riskType: x.riskType?.value,
                        copyStopLoss: x.copyStopLoss?.value || '',
                        copyTakeProfit: x.copyTakeProfit?.value || false,
                        lotMultiplierValue: x.lotMultiplierValue?.value || 0,
                        fixedLotValue: x.fixedLotValue?.value || 0,
                        copyPendingOrders: x.copyPendingOrders?.value || false,
                        forceMinimumLotSize: x.forceMinimumLotSize?.value || false,
                        forceMaximumLotSize: x.forceMaximumLotSize?.value || false,
                        copyComment: x.copyComment?.value || false,
                        customComment: x.customComment?.value || '',
                        platform: x.platform || '',
                        reverseTrade: x.reverseTrade?.value || false,
                        masterId: config?.master?.accountId || '',
                        strictClose: x.strictClose?.value || false,
                        considerContractSize: x.considerContractSize?.value || false,
                        copyMagicNumber: x.copyMagicNumber?.value || false,
                        magicNumber: x.magicNumber?.value === 0 ? '0' : x.magicNumber?.value || '',
                        fixedMasterBalance: x.fixedMasterBalance?.value || '',
                        fixedBalanceMultiplierValue: x.fixedBalanceMultiplierValue?.value || '',
                        lotRefiner: x.lotRefinerValue?.value,
                        tradeDelayMin: x.minDelayTime?.value,
                        tradeDelayMax: x.maxDelayTime?.value,
                        magicFilterList: x.magicFiltersList,
                        magicFilter: x.magicFiltersList
                          .filter((e) => e.value !== null && e.value !== undefined && e.value !== 0)
                          .map((data) => data.value),
                        commentFilterList: x.commentFiltersList,
                        commentFilter: x.commentFiltersList
                          .filter((e) => e.value !== null && e.value !== undefined && e.value !== '')
                          .map((data) => data.value),
                        minLotFilter: x.minLotFilter?.value,
                        maxLotFilter: x.maxLotFilter?.value,
                        copyExpiryTime: x.copyExpiryTime?.value,
                        fixedStopLossValue: x.stopLossPip?.value || 0,
                        fixedTakeProfitValue: x.takeProfitPip?.value || 0,
                        stopLossRefinementValue: x.slRefiner?.value || 0,
                        takeProfitRefinementValue: x.tpRefiner?.value || 0,
                        copyExistingTrades: x.copyExistingTrades?.value || false
                      },
                      expanded: false,
                      slaveData: x
                    };
                    return tmpSlave;
                  })
                };
                return tmp;
              });

              const deepConfigs = cloneDeep(configs);
              setListMasters(deepConfigs.filter((x) => x.isMaster));
              setConfigList(configs);
            }
            setLoading(false);
          })
          .catch((err) => {
            console.error('ERROR: ', err);
            const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
            service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
            setLoading(false);
          });
      })
      .catch((err) => {
        console.error('ERROR: ', err);
        const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
        service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
        setLoading(false);
      });
  };

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [openMenuRow, setOpenMenuRow] = useState<null | CopyTradingTableDto>(null);
  const [openPauseModal, setOpenPauseModal] = useState<boolean>(false);
  const [selectedHour, setSelectedHour] = useState<string>('0');
  const [selectedMinute, setSelectedMinute] = useState<string>('0');

  const [openSymbolSettingList, setOpenSymbolSettingList] = useState(false);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [selectedSlave, setSelectedSlave] = useState<CopyTradingTableDto | null>(null);

  useEffect(() => {
    if (!openPauseModal) {
      setSelectedHour('0');
      setSelectedMinute('0');
      setOpenMenuRow(null);
    }
  }, [openPauseModal]);
  const openMenu = Boolean(anchorEl);
  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, row: CopyTradingTableDto) => {
    setAnchorEl(event.currentTarget);
    setOpenMenuRow(row);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
    setOpenMenuRow(null);
  };

  const handleMasterSelection = (row: CopyTradingTableDto, checked: any) => {
    setConfigList((prev) => {
      if (!prev) {
        return prev;
      }

      // Map over the config list to ensure only the selected master and its children are checked
      const newConfigList = prev.map((config) => {
        if (config.configId === row.configId) {
          // Update the selected master and its children
          const updatedChildren = config.children?.map((child) => ({
            ...child,
            selected: checked
          }));

          return {
            ...config,
            selected: checked,
            children: updatedChildren
          };
        } else {
          // Deselect all other masters and their children
          const deselectedChildren = config.children?.map((child) => ({
            ...child,
            selected: false
          }));

          return {
            ...config,
            selected: false,
            children: deselectedChildren
          };
        }
      });

      return newConfigList;
    });
  };

  const handleSlaveSelection = (row: CopyTradingTableDto, checked: any) => {
    setConfigList((prev) => {
      if (!prev) {
        return prev;
      }

      // Map over the config list to ensure only the relevant slave's state is updated
      const newConfigList = prev.map((config) => {
        if (config.configId === row.configId) {
          // Only update the selected slave under the same master
          const updatedChildren = config.children?.map((child) => {
            if (child.id === row.id) {
              return {
                ...child,
                selected: checked
              };
            }
            return child; // Preserve the state of other children under the same master
          });

          // Update master selected state based on any checked slave
          const isAnyChildSelected = updatedChildren?.some((child) => child.selected);

          return {
            ...config,
            selected: isAnyChildSelected || checked,
            children: updatedChildren
          };
        } else {
          // Deselect all other masters and their children
          const deselectedChildren = config.children?.map((child) => ({
            ...child,
            selected: false
          }));

          return {
            ...config,
            selected: false,
            children: deselectedChildren
          };
        }
      });

      return newConfigList;
    });
  };

  const countSelectedSlaves = (): number => {
    if (!configList) {
      return 0;
    }

    return configList.reduce((total, config) => {
      if (config.children) {
        const selectedSlavesCount = config.children.filter((child) => child.selected).length;
        return total + selectedSlavesCount;
      }
      return total;
    }, 0);
  };

  const clearSelections = () => {
    setConfigList((prev) => {
      if (!prev) {
        return prev;
      }

      return prev.map((config) => {
        // Set master config selection to false
        const updatedConfig = { ...config, selected: false };

        // Check if there are children and set their selections to false
        if (updatedConfig.children) {
          updatedConfig.children = updatedConfig.children.map((child) => ({
            ...child,
            selected: false
          }));
        }

        return updatedConfig;
      });
    });
  };

  const allowDisbale = async (accountId: string, slaveIds: string[]) => {
    try {
      setLoading(true);
      const client = await accountClient();
      const req: OpenTradeWarningReq.AsObject = {
        userId: user?.tcId || '',
        accountId: accountId,
        slaveIdsList: slaveIds,
        isConfig: true
      };
      const res = await client.openTradeWarning(req);
      const result = res as OpenTradeWarningReply.AsObject;
      setLoading(false);
      return result.warning;
    } catch (err: any) {
      console.error('ERROR: ', err);
      const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
      service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
      setLoading(false);
      return null;
    }
  };

  const actionButtonStyle = {
    minWidth: 'max-content',
    height: sizeByBreakpoint(1, 36),
    minHeight: 'auto',
    backgroundColor: theme.palette.background.paper,
    border: '1px solid',
    borderColor: theme.palette.border.main,
    color: theme.palette.text.secondary,
    paddingX: sizeByBreakpoint(1, 10),
    paddingY: sizeByBreakpoint(1, 5),
    fontSize: sizeByBreakpoint(0.5, 14),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: sizeByBreakpoint(0.5, 5),
    '&:hover': {
      backgroundColor: theme.palette.background.paper,
      borderColor: theme.palette.border.main,
      color: theme.palette.text.secondary
    },
    boxShadow: '0px 1px 0px 0px #0000001A'
  };

  const actionButtonIconStyle = (isMaster = true) => {
    return {
      width: sizeByBreakpoint(0.5, 16),
      height: sizeByBreakpoint(0.5, 16),
      '& path': {
        fill: isMaster ? theme.palette.primary.main : theme.palette.warning.main
      },
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center'
    };
  };

  const columns = [
    {
      Header: '',
      accessor: 'selected',
      className: 'w-60',
      Cell: ({ row }: any) => (
        <Checkbox
          checked={row.original.selected}
          onChange={(event, checked) => handleMasterSelection(row.original, checked)}
          disableRipple
          sx={{
            ml: 0.5,
            '&:hover': {
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.04),
              borderRadius: 2
            }
          }}
          icon={
            <Box
              sx={{
                width: 18,
                height: 18,
                border: '1.5px solid',
                borderColor: theme.palette.grey[400],
                borderRadius: '4px'
              }}
            />
          }
          checkedIcon={
            <Box
              sx={{
                width: 18,
                height: 18,
                bgcolor: 'primary.main',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckOutlined style={{ fontSize: 11, color: 'white' }} />
            </Box>
          }
        />
      ),
      SubCell: ({ row }: any) => (
        <Checkbox
          checked={row.original.selected}
          onChange={(event, checked) => handleSlaveSelection(row.original, checked)}
          disableRipple
          sx={{
            '&:hover': {
              backgroundColor: (theme) =>
                theme.palette.mode === 'dark' ? alpha(theme.palette.primary.main, 0.2) : alpha(theme.palette.primary.main, 0.04),
              borderRadius: 2
            }
          }}
          icon={
            <Box
              sx={{
                width: 18,
                height: 18,
                border: '1.5px solid',
                borderColor: theme.palette.grey[400],
                borderRadius: '4px'
              }}
            />
          }
          checkedIcon={
            <Box
              sx={{
                width: 18,
                height: 18,
                bgcolor: 'primary.main',
                borderRadius: '4px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <CheckOutlined style={{ fontSize: 11, color: 'white' }} />
            </Box>
          }
        />
      ),
      disableSortBy: true
    },
    {
      Header: 'Account Name',
      accessor: 'accountName',
      Cell: ({ row: { original } }: any) => {
        return (
          <Grid display="flex" direction="column" alignItems="start" gap={0.5}>
            <Typography fontWeight={600} fontSize={textSizeByBreakpoint(14)}>
              {original.accountName}
            </Typography>
            <Chip
              label={original.isMaster ? 'MASTER' : 'SLAVE'}
              color={original.isMaster ? 'primary' : 'warning'}
              size="small"
              sx={{
                fontSize: sizeByBreakpoint(0.5, 10),
                fontWeight: 700,
                borderRadius: '2px',
                color: theme.palette.primary.contrastText,
                height: sizeByBreakpoint(1, 16),
                px: '3px !important',
                py: '2px !important',
                lineHeight: '100%',
                '& .MuiChip-label': {
                  padding: 0 + '!important'
                },
                ...(!original.isMaster &&
                  theme.palette.mode === 'dark' && {
                    backgroundColor: '#D68910',
                    color: '#1A1D1F'
                  })
              }}
            />
          </Grid>
        );
      }
    },
    {
      Header: 'Copy From',
      accessor: 'copyFrom',
      Cell: ({ row: { original } }: any) => {
        // Format platform display - convert MT-4/MT-5 to MT4/MT5
        const formatPlatform = (platform: string) => {
          if (platform === 'MT-4') return 'MT4';
          if (platform === 'MT-5') return 'MT5';
          return platform || '';
        };
        const platformLabel = formatPlatform(original.masterPlatform);
        return (
          <Grid display="flex" direction="column" alignItems="start" gap={0.5}>
            <Typography fontWeight={600} fontSize={textSizeByBreakpoint(14)} color="textSecondary">
              {original.copyFrom}
            </Typography>
            {!original.isMaster && platformLabel && (
              <Chip
                label={platformLabel}
                color="default"
                size="small"
                sx={{
                  fontSize: sizeByBreakpoint(0.5, 10),
                  fontWeight: 700,
                  borderRadius: '2px',
                  backgroundColor: '#6F7798',
                  color: theme.palette.primary.contrastText,
                  height: sizeByBreakpoint(1, 16),
                  px: '3px !important',
                  py: '2px !important',
                  lineHeight: '100%',
                  '& .MuiChip-label': {
                    padding: 0 + '!important'
                  }
                }}
              />
            )}
          </Grid>
        );
      }
    },
    {
      Header: 'Risk Type',
      accessor: 'riskType',
      Cell: ({ row: { original } }: any) => {
        return (
          <Typography fontWeight={600} fontSize={textSizeByBreakpoint(14)} color="textSecondary">
            {original.riskType}
          </Typography>
        );
      }
    },
    {
      Header: 'Risk Setting',
      accessor: 'riskSetting',
      Cell: ({ row: { original } }: any) => {
        return (
          <Typography fontWeight={600} fontSize={textSizeByBreakpoint(14)} color="textSecondary">
            {original.riskSetting}
          </Typography>
        );
      }
    },
    // {
    //   Header: 'Setting',
    //   accessor: 'setting'
    // },
    {
      Header: 'Status',
      accessor: 'status',
      Cell: ({ row: { original, index } }: any) => {
        // Determine status based on the data
        const getStatus = (): 'active' | 'paused' | 'disabled' => {
          // Check for time-based pause first
          if (original.pauseTimeSec && original.pauseTimeSec * 1000 > new Date().getTime()) {
            return 'paused';
          }
          // original.status: 'left' = disabled, 'right' = enabled, 'center' = manage existing (paused)
          if (original.status === 'right') return 'active';
          if (original.status === 'center') return 'paused';
          return 'disabled';
        };

        const handleStatusChange = async (newValue: 'active' | 'paused' | 'disabled') => {
          try {
            if (newValue === 'paused') {
              // Set "Manage existing" state - status 'center'
              if (original.isMaster) {
                toggleConfig(original, 'center');
              } else {
                toggleSlave(original, 'center');
              }
            } else if (newValue === 'active') {
              // Enable the account - status 'right'
              if (original.isMaster) {
                toggleConfig(original, 'right');
              } else {
                toggleSlave(original, 'right');
              }
            } else if (newValue === 'disabled') {
              // Disable the account - status 'left'
              if (original.isMaster) {
                toggleConfig(original, 'left');
              } else {
                toggleSlave(original, 'left');
              }
            }
          } catch (error) {
            console.error('Error updating status:', error);
          }
        };

        return (
          <Tooltip
            title={
              original.pauseTimeSec && original.pauseTimeSec * 1000 > new Date().getTime()
                ? `Copier paused until ${new Date(original.pauseTimeSec * 1000).toLocaleString()}`
                : ''
            }
            arrow
          >
            <Box>
              <ThreeStateToggle
                value={getStatus()}
                onChange={handleStatusChange}
                disabled={original.pauseTimeSec && original.pauseTimeSec * 1000 > new Date().getTime()}
              />
            </Box>
          </Tooltip>
        );
      }
    },
    {
      Header: 'Actions',
      accessor: '',
      Cell: ({ row }: any) => {
        const { original } = row;
        return (
          <Stack direction="row" alignItems="center" gap={sizeByBreakpoint(1, 10)}>
            {original.isMaster ? (
              <>
                <Button size="small" variant="contained" onClick={() => addSlaveModal(original)} sx={actionButtonStyle}>
                  <Box component={PlusIcon} sx={actionButtonIconStyle()} />
                  Add Slave
                </Button>

                <Tooltip
                  title={
                    original.pauseTimeSec * 1000 >= new Date().getTime()
                      ? `Copier paused until ${new Date(original.pauseTimeSec * 1000).toLocaleString()}`
                      : ''
                  }
                  arrow
                >
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      if (original.pauseTimeSec * 1000 < new Date().getTime()) {
                        handleMenuClick(e, original);
                      } else {
                        pauseAccount(0, original.configId);
                      }
                    }}
                    sx={actionButtonStyle}
                  >
                    <Box
                      component={original.pauseTimeSec * 1000 < new Date().getTime() ? PauseIcon : PlayCircleOutlined}
                      sx={{
                        ...actionButtonIconStyle(),
                        ...(original.pauseTimeSec * 1000 >= new Date().getTime()
                          ? {
                              '& path': {
                                fill: theme.palette.success.main
                              }
                            }
                          : {})
                      }}
                    />
                    {original.pauseTimeSec * 1000 < new Date().getTime() ? 'Pause' : 'Resume'}
                  </Button>
                </Tooltip>

                <Button size="small" variant="contained" onClick={() => deleteWarning(original)} sx={actionButtonStyle}>
                  <Box component={TrashIcon} sx={actionButtonIconStyle()} />
                  Delete
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  sx={{ ...actionButtonStyle, minWidth: '35px' }}
                  onClick={(e) => {
                    if (original.actualAccountId) {
                      const newExpandedState = !row.isExpanded;
                      // Persist expanded state to localStorage using functional update
                      setExpandedMasters((prev: Record<string, boolean>) => ({
                        ...prev,
                        [original.actualAccountId]: newExpandedState
                      }));
                      // Update configList to trigger re-render with new expanded state
                      setConfigList((prevList) =>
                        prevList.map((config) =>
                          config.actualAccountId === original.actualAccountId ? { ...config, expanded: newExpandedState } : config
                        )
                      );
                    }
                  }}
                >
                  <Box
                    component={row.isExpanded ? UpOutlined : DownOutlined}
                    sx={{
                      ...actionButtonIconStyle(),
                      mx: 0,
                      fontSize: textSizeByBreakpoint(12)
                    }}
                  />
                </Button>
              </>
            ) : (
              <>
                <Button size="small" variant="contained" onClick={() => editSlaveModal(original)} sx={actionButtonStyle}>
                  <Box component={EditIcon} sx={actionButtonIconStyle(false)} />
                  Edit
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    const masterId = original.additionalProps.masterId;
                    const slaveAccountId = original.actualAccountId;
                    navigate(`map-symbols?master_id=${masterId}&slave_id=${slaveAccountId}`, { replace: false });
                  }}
                  sx={actionButtonStyle}
                >
                  <Box component={DirectionIcon} sx={actionButtonIconStyle(false)} />
                  Symbol Mapping
                </Button>

                <Button
                  size="small"
                  variant="contained"
                  onClick={() => {
                    setSelectedMasterAccIDs({ accountID: original.copyFrom, actualAccountID: original.copyFromId });
                    setSelectedSlave(original);
                    setOpenSymbolSettingList(true);
                  }}
                  sx={actionButtonStyle}
                >
                  <Box component={ArrowIcon} sx={actionButtonIconStyle(false)} />
                  Override
                </Button>
                <Tooltip
                  title={
                    original.pauseTimeSec * 1000 >= new Date().getTime()
                      ? `Copier paused until ${new Date(original.pauseTimeSec * 1000).toLocaleString()}`
                      : ''
                  }
                  arrow
                >
                  <Button
                    size="small"
                    variant="contained"
                    onClick={(e) => {
                      if (original.pauseTimeSec * 1000 < new Date().getTime()) {
                        handleMenuClick(e, original);
                      } else {
                        pauseAccount(0, original.configId, original.id);
                      }
                    }}
                    sx={actionButtonStyle}
                  >
                    <Box
                      component={original.pauseTimeSec * 1000 < new Date().getTime() ? PauseIcon : PlayCircleOutlined}
                      sx={{
                        ...actionButtonIconStyle(false),
                        ...(original.pauseTimeSec * 1000 >= new Date().getTime()
                          ? {
                              '& path': {
                                fill: theme.palette.success.main
                              }
                            }
                          : {})
                      }}
                    />
                    {original.pauseTimeSec * 1000 < new Date().getTime() ? 'Pause' : 'Resume'}
                  </Button>
                </Tooltip>

                <Button size="small" variant="contained" onClick={() => handleEditTradingHour(original)} sx={actionButtonStyle}>
                  <Box component={ClockIcon} sx={actionButtonIconStyle(false)} />
                  Time
                </Button>

                <Button size="small" variant="contained" onClick={() => deleteWarning(original)} sx={actionButtonStyle}>
                  <Box component={TrashIcon} sx={actionButtonIconStyle(false)} />
                  Delete
                </Button>
              </>
            )}
          </Stack>
        );
      }
    }
  ];

  const allCols = useMemo(() => columns, [theme, configList, listMasters]);

  const pauseAccount = (time: number, configId: string, slaveId?: string) => {
    if (user && user?.tcId && configId) {
      setLoading(true);
      tradeClient()
        .then((client) => {
          const req: PauseAccountReq.AsObject = {
            userId: user?.tcId || '',
            configId: configId,
            pauseUntil: {
              seconds: time > 0 ? Math.round(new Date().getTime() / 1000) + time : 0,
              nanos: 0
            }
          };
          if (slaveId) {
            req.slaveId = {
              value: slaveId
            };
          }
          client
            .pauseAccount(req)
            .then((res) => {
              fetchConfigs();
              setLoading(false);
              setOpenPauseModal(false);
              if (time <= 0) {
                service.dispatchSnackbar(dispatch, 'Successfully resumed account', 'alert', 'success', 2000);
              } else {
                service.dispatchSnackbar(dispatch, 'Successfully paused account', 'alert', 'success', 2000);
              }
            })
            .catch((err) => {
              console.error('ERROR: ', err);
              setLoading(false);
              const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
              service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
            });
        })
        .catch((err) => {
          console.error('ERROR: ', err);
          setLoading(false);
          const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
          service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
        });
    }
  };

  const editSlaveModal = (row: CopyTradingTableDto) => {
    setSelectedRow(row);
    setEditSlaveModalOpen(true);
  };

  const handleEditTradingHour = (row: CopyTradingTableDto) => {
    setSelectedRow(row);
    setEditTradingHour(true);
  };

  const handleEditSlaveModalClose = () => {
    setEditSlaveModalOpen(false);
  };

  // const handleEditSlaveSuccess = () => {
  //   setEditSlaveModalOpen(false);
  //   fetchConfigs();
  // };

  // const handleEditSlavesSuccess = () => {
  //   setEditSlavesModalOpen(false);
  //   fetchConfigs();
  // };

  const addSlaveModal = (row: CopyTradingTableDto) => {
    setSelectedRow(row);
    setAddSlaveModalOpen(true);
  };

  const editSlavesModal = () => {
    const masterSelected = configList.find((x) => x.selected);
    if (!masterSelected) {
      service.dispatchSnackbar(dispatch, 'No master selected', 'alert', 'error', 5000);
      return;
    }

    const slavesSelected = masterSelected.children?.filter((x) => x.selected);
    if (!slavesSelected || slavesSelected.length <= 0) {
      service.dispatchSnackbar(dispatch, 'No slave selected', 'alert', 'error', 5000);
      return;
    }
    // setSelectedRow(row);
    setSelectedMaster(masterSelected);
    setSelectedSlaves(slavesSelected || []);
    setEditSlavesModalOpen(true);
  };

  // const handleEditSlavesClose = () => {
  //   setEditSlavesModalOpen(false);
  // };

  const handleAddSlaveModalClose = () => {
    setAddSlaveModalOpen(false);
  };

  const handleAddSlaveSuccess = () => {
    setAddSlaveModalOpen(false);
    fetchConfigs();
  };

  const deleteWarning = async (row: CopyTradingTableDto) => {
    const msz = await allowDisbale(row.actualAccountId, row.children ? row.children.map((x) => x.actualAccountId) : []);
    setSlaveWarn(!!msz);
    setWarnType('delete');
    setSelectedSlaves(null);
    setSelectedRow(row);
    setShowWarning(true);
  };

  const deleteConfig = () => {
    if (user && user?.tcId) {
      // Validate that we have the required fields
      if (!selectedRow?.configId) {
        service.dispatchSnackbar(dispatch, 'Invalid configuration ID', 'alert', 'error', 5000);
        console.error('Delete config failed: Missing configId', selectedRow);
        return;
      }

      tradeClient()
        .then((client) => {
          const req: DeleteConfigReq.AsObject = {
            userId: user?.tcId || '',
            configId: selectedRow?.configId || ''
          };

          console.log('Deleting config with request:', req);

          client
            .deleteConfig(req)
            .then((res) => {
              service.dispatchSnackbar(dispatch, 'Master Config deleted successfully', 'alert', 'success', 5000);

              // Clean up expanded state from local storage
              if (selectedRow?.actualAccountId) {
                const updatedExpandedState = { ...expandedMasters };
                delete updatedExpandedState[selectedRow.actualAccountId];
                setExpandedMasters(updatedExpandedState);
              }

              const crispData = {
                timestamp: new Date()?.toUTCString(),
                masterAccount: selectedRow?.accountId?.toString() || '',
                slaveAccounts: selectedRow?.children?.map((x) => x.accountId?.toString() || '').join(', ') || ''
              };
              Crisp.session.pushEvent('Copier Config Deleted', crispData, 'orange' as any);
              fetchConfigs();
            })
            .catch((err) => {
              console.error('ERROR: ', err);
              const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
              service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
            });
        })
        .catch((err) => {
          console.error('ERROR: ', err);
          const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
          service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
        });
    }
  };

  const deleteSlave = (configId?: string, slaveIds?: string[]) => {
    if (user && user?.tcId) {
      tradeClient()
        .then((client) => {
          const req: DeleteSalvesReq.AsObject = {
            userId: user?.tcId || '',
            configId: selectedRow?.configId || '',
            slaveAccountsList: [selectedRow?.id || '']
          };
          if (slaveIds && slaveIds.length > 0 && configId) {
            req.configId = configId;
            req.slaveAccountsList = slaveIds;
          }
          client
            .deleteSlave(req)
            .then((res) => {
              const msz = slaveIds && slaveIds.length > 0 ? 'Slaves deleted succssfully' : 'Slave deleted succssfully';
              service.dispatchSnackbar(dispatch, msz, 'alert', 'success', 5000);
              const crispData = {
                timestamp: new Date()?.toUTCString(),
                masterAccount: selectedRow?.masterAccountNumber?.toString() || '',
                slaveAccounts: selectedRow?.accountId?.toString() || ''
              };
              Crisp.session.pushEvent('Slave Config Removed', crispData, 'orange' as any);
              fetchConfigs();
            })
            .catch((err) => {
              console.error('ERROR: ', err);
              const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
              service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
            });
        })
        .catch((err) => {
          console.error('ERROR: ', err);
          const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
          service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
        });
    }
  };

  const deleteRow = () => {
    if (selectedSlaves && selectedSlaves.length > 1 && selectedMaster) {
      const slaveIds = selectedSlaves.map((x) => x.id);
      deleteSlave(selectedMaster.configId, slaveIds);
    } else if (selectedRow?.isMaster) {
      deleteConfig();
    } else if (!selectedRow?.isMaster) {
      deleteSlave();
    }
  };

  // const handleToggle = async (row: CopyTradingTableDto, checked: 'left' | 'right' | 'center') => {
  //   console.log('handleToggle: ', row, checked);
  //   if (row.isMaster) {
  //     if (checked !== 'right') {
  //       const msz = await allowDisbale(row.actualAccountId, row.children ? row.children.map((x) => x.actualAccountId) : []);
  //       if (msz) {
  //         setSlaveWarn(!!msz);
  //         setCheckedVal(checked);
  //         setSelectedRow(row);
  //         setWarnType('disable');
  //         setShowWarning(true);
  //         return;
  //       }
  //     }
  //     setSelectedRow(null);
  //     setWarnType(null);
  //     setShowWarning(false);
  //     toggleConfig(row, checked);
  //   } else {
  //     if (checked !== 'right') {
  //       const msz = await allowDisbale(row.actualAccountId, row.children ? row.children.map((x) => x.actualAccountId) : []);
  //       if (msz) {
  //         setSlaveWarn(!!msz);
  //         setCheckedVal(checked);
  //         setSelectedRow(row);
  //         setWarnType('disable');
  //         setShowWarning(true);
  //         return;
  //       }
  //     }
  //     setSelectedRow(null);
  //     setWarnType(null);
  //     setShowWarning(false);
  //     toggleSlave(row, checked);
  //   }
  // };

  const toggleConfig = (row: CopyTradingTableDto, status: 'left' | 'right' | 'center') => {
    tradeClient()
      .then((client) => {
        let req: UpdateConfigReq.AsObject = {
          userId: user?.tcId || '',
          configId: row.configId,
          masterAccountId: row.actualAccountId
        };
        if (status === 'left') {
          req.manageExisting = {
            value: false
          };
          req.enable = {
            value: false
          };
        }
        if (status === 'right') {
          req.manageExisting = {
            value: false
          };
          req.enable = {
            value: true
          };
        }
        if (status === 'center') {
          req.manageExisting = {
            value: true
          };
          req.enable = {
            value: false
          };
        }
        client
          .updateConfig(req)
          .then((res) => {
            if (status === 'right') {
              const crispData = {
                timestamp: new Date()?.toUTCString(),
                masterAccount: row?.accountId?.toString() || '',
                slaveAccounts: row?.children?.map((x) => x.accountId?.toString() || '') || []
              };
              Crisp.session.pushEvent('Master Config Enabled', crispData, 'green' as any);
            } else if (status === 'center') {
              const crispData = {
                timestamp: new Date()?.toUTCString(),
                masterAccount: row?.accountId?.toString() || '',
                slaveAccounts: row?.children?.map((x) => x.accountId?.toString() || '') || []
              };
              Crisp.session.pushEvent('Master Config Disabled - managing existing trade', crispData, 'yellow' as any);
            } else {
              const crispData = {
                timestamp: new Date()?.toUTCString(),
                masterAccount: row?.accountId?.toString() || '',
                slaveAccounts: row?.children?.map((x) => x.accountId?.toString() || '') || []
              };
              Crisp.session.pushEvent('Master Config Disabled', crispData, 'orange' as any);
            }
            fetchConfigs();
            if (status === 'left') {
              service.dispatchSnackbar(dispatch, `Master copier disabled successfully`, 'alert', 'success', 2000);
            } else if (status === 'center') {
              service.dispatchSnackbar(dispatch, `Master copier disabled - managing existing trade`, 'alert', 'success', 2000);
            } else {
              service.dispatchSnackbar(dispatch, `Master copier enabled successfully`, 'alert', 'success', 2000);
            }
            setShowWarning(false);
            setSelectedMaster(null);
            setSelectedSlaves([]);
            setSelectedRow(null);
          })
          .catch((err) => {
            console.error('ERROR: ', err);
            const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
            service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
            const tmpConf = [...configList];
            const idx = tmpConf.findIndex((x) => x.id === row.id);
            if (idx >= 0) {
              tmpConf[idx].status = status;
            }

            setConfigList(tmpConf);
            setLoading(true);
            fetchConfigs();
          });
      })
      .catch((err) => {
        console.error('ERROR: ', err);
        const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
        service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
        const tmpConf = [...configList];
        const idx = tmpConf.findIndex((x) => x.id === row.id);
        if (idx >= 0) {
          tmpConf[idx].status = status;
        }

        setConfigList(tmpConf);
        setLoading(true);
        fetchConfigs();
      });
  };

  const toggleSlave = (row: CopyTradingTableDto, status: 'left' | 'right' | 'center') => {
    tradeClient()
      .then((client) => {
        let req: UpdateSlaveReq.AsObject = {
          userId: user?.tcId || '',
          configId: row.configId,
          slaveId: row.id,
          riskType: risk_type_map_value[row?.slaveData?.riskType?.value || 'UNKNOWN'],
          lotMultiplierValue: row?.slaveData?.lotMultiplierValue,
          fixedLotValue: row?.slaveData?.fixedLotValue,
          balanceMultiplierValue: row?.slaveData?.balanceMultiplierValue,
          copyTakeProfit: row?.slaveData?.copyTakeProfit,
          copyStopLoss: row?.slaveData?.copyStopLoss,
          copyPendingOrders: row?.slaveData?.copyPendingOrders,
          forceMinimumLotSize: row?.slaveData?.forceMinimumLotSize,
          forceMaximumLotSize: row?.slaveData?.forceMaximumLotSize,
          copyComment: row?.slaveData?.copyComment,
          customComment: row?.slaveData?.customComment,
          reverseTrade: row?.slaveData?.reverseTrade,
          strictClose: row?.slaveData?.strictClose,
          considerContractSize: row?.slaveData?.considerContractSize,
          copyMagicNumber: row?.slaveData?.copyMagicNumber,
          magicNumber: row?.slaveData?.magicNumber,
          lotRefinerValue: row?.slaveData?.lotRefinerValue,
          magicFiltersList: row?.slaveData?.magicFiltersList || [],
          copyExpiryTime: row?.slaveData?.copyExpiryTime,
          commentFiltersList: row?.slaveData?.commentFiltersList || [],
          minLotFilter: row?.slaveData?.minLotFilter,
          maxLotFilter: row?.slaveData?.maxLotFilter,
          minDelayTime: row?.slaveData?.minDelayTime,
          maxDelayTime: row?.slaveData?.maxDelayTime,
          fixedMasterBalance: row?.slaveData?.fixedMasterBalance,
          fixedBalanceMultiplierValue: row?.slaveData?.fixedBalanceMultiplierValue,
          slRefiner: row.slaveData?.slRefiner,
          tpRefiner: row.slaveData?.tpRefiner,
          stopLossPip: row.slaveData?.stopLossPip,
          takeProfitPip: row.slaveData?.takeProfitPip,
          balancePercentageValue: row?.slaveData?.balancePercentageValue,
          copyExistingTrades: row?.slaveData?.copyExistingTrades,
          allowedTradeSide: row?.slaveData?.allowedTradeSide,
          tradingHoursList: row?.slaveData?.tradingHoursList || [],
          commentFilterType: filter_type_map_value[row?.slaveData?.commentFilterType?.value || 'UNKNOWN'],
          magicFilterType: filter_type_map_value[row?.slaveData?.magicFilterType?.value || 'UNKNOWN']
        };
        if (status === 'left') {
          req.manageExisting = {
            value: false
          };
          req.enable = {
            value: false
          };
        }
        if (status === 'right') {
          req.manageExisting = {
            value: false
          };
          req.enable = {
            value: true
          };
        }
        if (status === 'center') {
          req.manageExisting = {
            value: true
          };
          req.enable = {
            value: false
          };
        }
        client
          .updateSlave(req)
          .then((res) => {
            if (status === 'left') {
              const crispData = {
                timestamp: new Date()?.toUTCString(),
                masterAccount: row?.masterAccountNumber?.toString() || '',
                slaveAccounts: row?.accountId?.toString() || ''
              };
              Crisp.session.pushEvent('Slave Config Disabled', crispData, 'orange' as any);
            } else if (status === 'center') {
              const crispData = {
                timestamp: new Date()?.toUTCString(),
                masterAccount: row?.masterAccountNumber?.toString() || '',
                slaveAccounts: row?.accountId?.toString() || ''
              };
              Crisp.session.pushEvent('Slave copier disabled - managing existing trade', crispData, 'yellow' as any);
            } else {
              const crispData = {
                timestamp: new Date()?.toUTCString(),
                masterAccount: row?.masterAccountNumber?.toString() || '',
                slaveAccounts: row?.accountId?.toString() || ''
              };
              Crisp.session.pushEvent('Slave Config Enabled', crispData, 'green' as any);
            }
            setShowWarning(false);
            setSelectedMaster(null);
            setSelectedSlaves([]);
            setSelectedRow(null);
            fetchConfigs();
            if (status === 'left') {
              service.dispatchSnackbar(dispatch, `Slave copier disabled successfully`, 'alert', 'success', 2000);
            } else if (status === 'center') {
              service.dispatchSnackbar(dispatch, `Slave copier disabled - managing existing trade`, 'alert', 'success', 2000);
            } else {
              service.dispatchSnackbar(dispatch, `Slave copier enabled successfully`, 'alert', 'success', 2000);
            }
          })
          .catch((err) => {
            console.error('ERROR: ', err);
            const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
            service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
            const tmpConf = [...configList];
            const idx = tmpConf.findIndex((x) => x.id === row.id);
            if (idx >= 0) {
              tmpConf[idx].status = status;
            }

            setConfigList(tmpConf);
            setLoading(true);
            fetchConfigs();
          });
      })
      .catch((err) => {
        console.error('ERROR: ', err);
        const msz = err.message === 'no permission to access this RPC' ? 'Permission denied' : err.message;
        service.dispatchSnackbar(dispatch, msz, 'alert', 'error', 5000);
        const tmpConf = [...configList];
        const idx = tmpConf.findIndex((x) => x.id === row.id);
        if (idx >= 0) {
          tmpConf[idx].status = status;
        }

        setConfigList(tmpConf);
        setLoading(true);
        fetchConfigs();
      });
  };

  const getWarnMessage = (action: string) =>
    `This account has slaves that have opened trades. If you ${action} this config, the rest of the operations will not take effect on the slave trades.`;

  const getSlaveWarnMessage = (action: string) =>
    `This account has open trades. If you ${action} this config, the rest of the operations will not take effect on the trades.`;

  const [showConfirmClose, setShowConfirmClose] = useState(false);
  const [hasUnsaved, setHasUnsaved] = useState(false);

  return (
    <>
      {loading && <Loader />}
      <TableWrapper
        title="Trade Copiers"
        subTitle="Showing the list of all the accounts."
        headerSx={{
          marginBottom: sizeByBreakpoint(1, 10)
        }}
      >
        <Box width="100%" overflow="auto">
          <ExpandingTable data={configList} allColumns={allCols} isLoading={loading} expanded={true} />
        </Box>
      </TableWrapper>

      <CopierEditDialog
        isOpen={editSlaveModalOpen}
        onClose={() => setEditSlaveModalOpen(false)}
        selectedSlave={selectedRow}
        selectedMaster={configList.find((config) => config.children?.some((child) => child.id === selectedRow?.id))}
        onSuccess={() => {
          setEditSlaveModalOpen(false);
          fetchConfigs();
        }}
      />
      <CopierOverrideDialog isOpen={openSymbolSettingList} onClose={() => setOpenSymbolSettingList(false)} selectedSlave={selectedSlave} />
      <CopierAddTradingHoursDialog
        isOpen={editTradingHour}
        onClose={() => setEditTradingHour(false)}
        selectedSlave={selectedRow}
        selectedMaster={configList.find((config) => config.children?.some((child) => child.id === selectedRow?.id))}
        onSuccess={() => {
          setEditTradingHour(false);
          fetchConfigs();
        }}
      />
      {editSlavesModalOpen && selectedMaster && selectedSlaves && (
        <CopierBulkEditDialog
          isOpen={editSlavesModalOpen}
          onClose={() => {
            setEditSlavesModalOpen(false);
            setSelectedSlaves(null);
            setSelectedMaster(null);
          }}
          selectedMaster={selectedMaster}
          selectedSlaves={selectedSlaves}
          onSuccess={() => {
            setEditSlavesModalOpen(false);
            setSelectedSlaves(null);
            setSelectedMaster(null);
            fetchConfigs();
          }}
        />
      )}

      {countSelectedSlaves() > 0 && (
        <CustomConfigSnackbar
          action={true}
          duration={-1}
          open={true}
          message={`${countSelectedSlaves()} Slave${countSelectedSlaves() > 1 ? 's' : ''} selected`}
          editButton={'Bulk Edit'}
          deleteButton={'Delete'}
          handleEdit={() => {
            if (countSelectedSlaves() > 1) {
              editSlavesModal();
            } else {
              const masterSelected = configList.find((x) => x.selected);
              if (!masterSelected) {
                service.dispatchSnackbar(dispatch, 'No master selected', 'alert', 'error', 5000);
                return;
              }

              const slavesSelected = masterSelected.children?.filter((x) => x.selected);
              if (!slavesSelected || slavesSelected.length <= 0) {
                service.dispatchSnackbar(dispatch, 'No slave selected', 'alert', 'error', 5000);
                return;
              }
              editSlaveModal(slavesSelected[0]);
            }
            // localStorage.setItem('CURRENT_VERSION_' + ENV_NAME, version);
            // window.location.reload();
          }}
          handleDelete={async () => {
            const masterSelected = configList.find((x) => x.selected);
            if (!masterSelected) {
              service.dispatchSnackbar(dispatch, 'No master selected', 'alert', 'error', 5000);
              return;
            }

            const slavesSelected = masterSelected.children?.filter((x) => x.selected);
            if (!slavesSelected || slavesSelected.length <= 0) {
              service.dispatchSnackbar(dispatch, 'No slave selected', 'alert', 'error', 5000);
              return;
            }

            setWarnType('delete');
            if (slavesSelected && slavesSelected.length <= 1) {
              setSelectedRow(slavesSelected[0]);
              setShowWarning(true);
            } else {
              setSelectedSlaves(slavesSelected);
              setSelectedMaster(masterSelected);
              setShowWarning(true);
            }
            // localStorage.setItem('CURRENT_VERSION_' + ENV_NAME, version);
            // window.location.reload();
          }}
          handleClear={() => clearSelections()}
          alert={{
            color: 'error'
          }}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transition={'SlideUp'}
        />
      )}
      <Menu
        key={'menu'}
        id="fade-menu"
        MenuListProps={{
          'aria-labelledby': 'fade-button'
        }}
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleMenuClose}
        TransitionComponent={Fade}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left'
        }}
      >
        <MenuItem
          onClick={() => {
            if (openMenuRow?.isMaster) {
              pauseAccount(900, openMenuRow?.configId || '');
            } else {
              pauseAccount(900, openMenuRow?.configId || '', openMenuRow?.id);
            }
            handleMenuClose();
          }}
        >
          Pause for 15 minutes
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (openMenuRow?.isMaster) {
              pauseAccount(1800, openMenuRow?.configId || '');
            } else {
              pauseAccount(1800, openMenuRow?.configId || '', openMenuRow?.id);
            }
            handleMenuClose();
          }}
        >
          Pause for 30 minutes
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (openMenuRow?.isMaster) {
              pauseAccount(3600, openMenuRow?.configId || '');
            } else {
              pauseAccount(3600, openMenuRow?.configId || '', openMenuRow?.id);
            }
            handleMenuClose();
          }}
        >
          Pause for 1 hour
        </MenuItem>
        <MenuItem
          onClick={() => {
            setOpenPauseModal(true);
            setAnchorEl(null);
            // handleMenuClose();
          }}
        >
          Custom
        </MenuItem>
      </Menu>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={openPauseModal}
        onClose={() => setOpenPauseModal(false)}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={openPauseModal}>
          <Box sx={{ ...styleSymbolModal, minWidth: 320, maxWidth: 480 }}>
            <MainCard
              title={
                <Typography fontWeight={700} fontSize={16}>
                  Pause duration
                </Typography>
              }
              secondary={
                <IconButton
                  size="small"
                  onClick={() => {
                    setOpenPauseModal(false);
                  }}
                >
                  <CloseOutlined />
                </IconButton>
              }
              sx={{ minHeight: 240 }}
            >
              <Grid container spacing={3}>
                <Grid item xs={12} display={'flex'}>
                  <Grid sx={{ mr: 2 }} item xs={12}>
                    <Stack>
                      <FormControl variant="outlined">
                        <InputLabel sx={{ minHeight: '14px', lineHeight: '12px' }} htmlFor="hour">
                          Hours
                        </InputLabel>
                        <OutlinedInput
                          fullWidth
                          label={'Hour'}
                          inputProps={{ style: { minHeight: '14px', lineHeight: '12px' } }}
                          id="hour"
                          type="number"
                          value={selectedHour}
                          name="hour"
                          onChange={(e) => {
                            const inputValue = e.target.value || '';
                            if (inputValue === '') {
                              setSelectedHour('');
                            } else if (/^\d*$/.test(inputValue)) {
                              setSelectedHour(inputValue);
                            }
                          }}
                          placeholder="Hour"
                          endAdornment={<InputAdornment position="end">hr</InputAdornment>}
                        />
                      </FormControl>
                    </Stack>
                  </Grid>
                  <Grid item xs={12}>
                    <Stack>
                      <FormControl variant="outlined">
                        <InputLabel sx={{ minHeight: '14px', lineHeight: '12px' }} htmlFor="minute">
                          Minutes
                        </InputLabel>
                        <OutlinedInput
                          fullWidth
                          label={'Minute'}
                          inputProps={{ style: { minHeight: '14px', lineHeight: '12px' } }}
                          id="minute"
                          type="number"
                          value={selectedMinute}
                          name="minute"
                          onChange={(e) => {
                            const inputValue = e.target.value || '';
                            if (inputValue === '') {
                              setSelectedMinute('');
                            } else if (/^\d*$/.test(inputValue)) {
                              const numericValue = parseInt(inputValue, 10);
                              if (numericValue >= 0 && numericValue <= 59) {
                                setSelectedMinute(e.target.value);
                              }
                            }
                          }}
                          placeholder="Minute"
                          endAdornment={<InputAdornment position="end">min</InputAdornment>}
                        />
                      </FormControl>
                    </Stack>
                  </Grid>
                </Grid>
                <Grid item xs={12} sx={{ mt: 4 }} display="flex" justifyContent="end" alignItems="flex-start">
                  <div style={{ marginRight: '8px' }}>
                    <Button
                      sx={{
                        paddingX: '24px',
                        color: (theme) => (theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.text.secondary),
                        borderColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.grey[600] : theme.palette.grey[300]),
                        '&:hover': {
                          borderColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.common.white : theme.palette.grey[500]),
                          backgroundColor: (theme) => (theme.palette.mode === 'dark' ? theme.palette.action.hover : theme.palette.grey[50])
                        }
                      }}
                      onClick={() => setOpenPauseModal(false)}
                      disableElevation
                      fullWidth
                      variant="outlined"
                      color="secondary"
                    >
                      Cancel
                    </Button>
                  </div>
                  <div>
                    <LoadingButton
                      sx={{ paddingX: '24px' }}
                      loadingPosition="end"
                      endIcon={<></>}
                      loading={loading}
                      disableElevation
                      disabled={loading}
                      fullWidth
                      type="submit"
                      variant="contained"
                      color="primary"
                      onClick={() => {
                        try {
                          let totalSec = 0;
                          const hour = parseInt(selectedHour);
                          const min = parseFloat(selectedMinute);
                          if (hour) {
                            totalSec = totalSec + hour * 3600;
                          }
                          if (min) {
                            totalSec = totalSec + min * 60;
                          }
                          if (openMenuRow?.isMaster) {
                            pauseAccount(totalSec, openMenuRow?.configId || '');
                          } else {
                            pauseAccount(totalSec, openMenuRow?.configId || '', openMenuRow?.id);
                          }
                        } catch (e) {
                          console.error('ERROR: ', e);
                          service.dispatchSnackbar(dispatch, 'Invalid input', 'alert', 'error', 5000);
                        }
                      }}
                    >
                      Confirm
                    </LoadingButton>
                  </div>
                </Grid>
              </Grid>
            </MainCard>
          </Box>
        </Fade>
      </Modal>
      <Dialog
        maxWidth="sm"
        fullWidth
        onClose={() => {
          if (selectedRow && checkedVal && selectedRow.isMaster) {
            const tmpConf = [...configList];
            const idx = tmpConf.findIndex((x) => x.id === selectedRow.id);
            if (idx >= 0) {
              tmpConf[idx].status = checkedVal;
            }
            setConfigList(tmpConf);
          }
          if (selectedRow && checkedVal && !selectedRow.isMaster) {
            const tmpConf = configList.map((config) => {
              if (config.id === selectedRow.parentId) {
                const updatedChildren = config.children?.map((child) =>
                  child.id === selectedRow.id ? { ...child, status: checkedVal } : child
                );
                return { ...config, children: updatedChildren };
              }
              return config;
            });
            setConfigList(tmpConf);
          }
          setShowWarning(false);
          setSelectedMaster(null);
          setSelectedSlaves([]);
          setSelectedRow(null);
          setWarnType(null);
          fetchConfigs();
        }}
        open={showWarning}
        sx={{ '& .MuiDialog-paper': { p: 0 } }}
      >
        {showWarning && (
          <>
            <Box sx={{ p: 1, py: 1.5 }}>
              <DialogTitle id="alert-dialog-title">
                {selectedRow && selectedRow?.isMaster && warnType === 'delete' && 'Are you sure, you want to delete master configuration?'}
                {selectedRow &&
                  selectedRow?.isMaster &&
                  warnType === 'disable' &&
                  'Are you sure, you want to disable master configuration?'}
                {selectedRow &&
                  !selectedRow?.isMaster &&
                  warnType === 'disable' &&
                  'Are you sure, you want to disable slave configuration?'}
                {selectedRow && !selectedRow?.isMaster && warnType === 'delete' && 'Are you sure, you want to delete slave configuration?'}
                {!selectedRow && selectedSlaves && selectedSlaves?.length > 1 && 'Are you sure, you want to delete slave configurations?'}
              </DialogTitle>
              <DialogContent>
                <DialogContentText id="alert-dialog-description">
                  {selectedRow &&
                    selectedRow?.isMaster &&
                    warnType === 'delete' &&
                    `You are going to delete master configuration for account ${selectedRow.accountName}. ${
                      slaveWarn ? getWarnMessage('delete') : ''
                    }`}
                  {selectedRow &&
                    selectedRow?.isMaster &&
                    warnType === 'disable' &&
                    `You are going to disable master configuration for account ${selectedRow.accountName}. ${
                      slaveWarn ? getWarnMessage('disable') : ''
                    }`}
                  {selectedRow &&
                    !selectedRow?.isMaster &&
                    warnType === 'disable' &&
                    `You are going to disable slave configuration for account ${selectedRow.accountName}. ${
                      slaveWarn ? getSlaveWarnMessage('disable') : ''
                    }`}
                  {selectedRow &&
                    !selectedRow?.isMaster &&
                    warnType === 'delete' &&
                    `You are going to delete slave configuration for account ${selectedRow.accountName}. ${
                      slaveWarn ? getSlaveWarnMessage('delete') : ''
                    }`}
                  {!selectedRow &&
                    selectedSlaves &&
                    selectedSlaves?.length > 1 &&
                    `You are going to delete slave configurations for accounts ${selectedSlaves.map((x) => x.accountName)}`}
                </DialogContentText>
              </DialogContent>
              <DialogActions>
                <Button
                  color="error"
                  onClick={() => {
                    if (selectedRow && checkedVal && selectedRow.isMaster) {
                      const tmpConf = [...configList];
                      const idx = tmpConf.findIndex((x) => x.id === selectedRow.id);
                      if (idx >= 0) {
                        tmpConf[idx].status = checkedVal;
                      }
                      setConfigList(tmpConf);
                    }
                    if (selectedRow && checkedVal && !selectedRow.isMaster) {
                      const tmpConf = configList.map((config) => {
                        if (config.id === selectedRow.parentId) {
                          const updatedChildren = config.children?.map((child) =>
                            child.id === selectedRow.id ? { ...child, status: checkedVal } : child
                          );
                          return { ...config, children: updatedChildren };
                        }
                        return config;
                      });
                      setConfigList(tmpConf);
                    }

                    setShowWarning(false);
                    setSelectedMaster(null);
                    setSelectedSlaves([]);
                    setSelectedRow(null);
                    fetchConfigs();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={() => {
                    if (warnType === 'disable') {
                      if (selectedRow && checkedVal && selectedRow.isMaster) {
                        toggleConfig(selectedRow, checkedVal);
                      } else if (selectedRow && checkedVal && !selectedRow.isMaster) {
                        toggleSlave(selectedRow, checkedVal);
                      }
                    } else {
                      deleteRow();
                      setShowWarning(false);
                      setSelectedMaster(null);
                      setSelectedSlaves([]);
                      setSelectedRow(null);
                    }
                  }}
                  autoFocus
                >
                  Confirm
                </Button>
              </DialogActions>
            </Box>
          </>
        )}
      </Dialog>
      <Drawer
        anchor="right"
        open={addSlaveModalOpen}
        onClose={() => {
          if (addSlaveFormState === 0) {
            if (hasUnsaved) {
              setShowConfirmClose(true);
              return;
            }
            handleAddSlaveModalClose();
          }
        }}
        PaperProps={{
          sx: {
            boxShadow: 'none',
            backgroundImage: 'none',
            height: '100dvh',
            maxHeight: '100dvh',
            display: 'flex',
            flexDirection: 'column',
            // Fallback for browsers that don't support dvh
            '@supports not (height: 100dvh)': {
              height: '100vh',
              maxHeight: '100vh'
            }
          }
        }}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1000
        }}
      >
        <Stack
          sx={{
            width: {
              xs: '100%',
              sm: '600px',
              md: '640px',
              lg: '680px',
              xl: '720px'
            },
            height: '100%',
            maxHeight: '100dvh',
            borderTopLeftRadius: '40px',
            borderBottomLeftRadius: '40px',
            backgroundColor: (theme) => theme.palette.background.paper,
            padding: { xl: 4.5, lg: 4, md: 3.5, sm: 3, xs: 2.5 },
            paddingBottom: { xs: 'env(safe-area-inset-bottom, 16px)', sm: 0 },
            overflow: 'hidden',
            // Fallback for browsers that don't support dvh
            '@supports not (max-height: 100dvh)': {
              maxHeight: '100vh'
            }
          }}
          display="flex"
          flexDirection="column"
          gap={2}
        >
          <Box
            sx={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <Box
              sx={{
                flex: 1,
                overflow: 'auto',
                display: 'flex',
                flexDirection: 'column'
              }}
            >
              <AddSlaveForm
                handleClose={(val) => {
                  if (val) {
                    setShowConfirmClose(true);
                    return;
                  }
                  handleAddSlaveSuccess();
                  handleAddSlaveModalClose();
                }}
                checkHasUnsaved={(val) => {
                  setHasUnsaved(val);
                }}
                getFormState={(state) => setAddSlaveFormState(state)}
                handleSuccess={handleAddSlaveSuccess}
                selectedConfigId={selectedRow?.configId ? selectedRow?.configId : ''}
                masterAccountId={selectedRow?.actualAccountId || ''}
              />
            </Box>
          </Box>
        </Stack>
      </Drawer>

      <ConfirmCloseDialog
        showConfirmClose={showConfirmClose}
        handleOnchange={(value) => {
          setShowConfirmClose(false);
          if (value === 'DISCARD') {
            handleAddSlaveSuccess();
            handleAddSlaveModalClose();
            handleEditSlaveModalClose();
            setEditTradingHour(false);
          }
        }}
      />

      {/*<Modal*/}
      {/*  aria-labelledby="transition-modal-title"*/}
      {/*  aria-describedby="transition-modal-description"*/}
      {/*  open={editSlaveModalOpen}*/}
      {/*  onClose={() => {*/}
      {/*    if (hasUnsaved) {*/}
      {/*      setShowConfirmClose(true);*/}
      {/*      return;*/}
      {/*    }*/}
      {/*    handleEditSlaveModalClose();*/}
      {/*  }}*/}
      {/*  closeAfterTransition*/}
      {/*  BackdropComponent={Backdrop}*/}
      {/*  BackdropProps={{*/}
      {/*    timeout: 500*/}
      {/*  }}*/}
      {/*>*/}
      {/*  <Fade in={editSlaveModalOpen}>*/}
      {/*    <Box sx={{ ...style, p: 0, minWidth: 820, maxWidth: 1200, whiteSpace: 'nowrap' }}>*/}
      {/*      <EditSlaveForm*/}
      {/*        handleClose={(val) => {*/}
      {/*          if (val) {*/}
      {/*            setShowConfirmClose(true);*/}
      {/*            return;*/}
      {/*          }*/}
      {/*          handleEditSlaveModalClose();*/}
      {/*        }}*/}
      {/*        checkHasUnsaved={(val) => {*/}
      {/*          setHasUnsaved(val);*/}
      {/*        }}*/}
      {/*        handleSuccess={handleEditSlaveSuccess}*/}
      {/*        selectedConfigId={selectedRow?.configId ? selectedRow?.configId : ''}*/}
      {/*        selectedSlave={selectedRow}*/}
      {/*        selectedMaster={listMasters.find((x) => x.configId === selectedRow?.configId) || null}*/}
      {/*      />*/}
      {/*    </Box>*/}
      {/*  </Fade>*/}
      {/*</Modal>*/}

      {/*{selectedRow && !selectedRow?.isMaster && (*/}
      {/*  <Modal*/}
      {/*    aria-labelledby="transition-modal-title"*/}
      {/*    aria-describedby="transition-modal-description"*/}
      {/*    open={editTradingHour}*/}
      {/*    onClose={() => {*/}
      {/*      if (hasUnsaved) {*/}
      {/*        setShowConfirmClose(true);*/}
      {/*        return;*/}
      {/*      }*/}
      {/*      setEditTradingHour(false);*/}
      {/*    }}*/}
      {/*    closeAfterTransition*/}
      {/*    BackdropComponent={Backdrop}*/}
      {/*    BackdropProps={{*/}
      {/*      timeout: 500*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    <Fade in={editTradingHour}>*/}
      {/*      <Box sx={{ ...style, p: 0, minWidth: 820, maxWidth: 1200, whiteSpace: 'nowrap' }}>*/}
      {/*        <TradingHourForm*/}
      {/*          handleClose={(val) => {*/}
      {/*            if (val) {*/}
      {/*              setShowConfirmClose(true);*/}
      {/*              return;*/}
      {/*            }*/}
      {/*            setEditTradingHour(false);*/}
      {/*          }}*/}
      {/*          checkHasUnsaved={(val) => {*/}
      {/*            setHasUnsaved(val);*/}
      {/*          }}*/}
      {/*          handleSuccess={() => {*/}
      {/*            setEditTradingHour(false);*/}
      {/*            fetchConfigs();*/}
      {/*          }}*/}
      {/*          selectedConfigId={selectedRow?.configId ? selectedRow?.configId : ''}*/}
      {/*          selectedSlave={selectedRow}*/}
      {/*          selectedMaster={listMasters.find((x) => x.configId === selectedRow?.configId) || null}*/}
      {/*        />*/}
      {/*      </Box>*/}
      {/*    </Fade>*/}
      {/*  </Modal>*/}
      {/*)}*/}

      {/* <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={editSlavesModalOpen}
        onClose={handleEditSlavesClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{
          timeout: 500
        }}
      >
        <Fade in={editSlavesModalOpen}>
          <Box sx={{ ...style, p: 0, minWidth: 560, maxWidth: 720 }}>
            <EditSlaveManyForm
              handleClose={handleEditSlavesClose}
              handleSuccess={handleEditSlaveSuccess}
              selectedConfigId={selectedRow?.configId ? selectedRow?.configId : ''}
              selectedSlaves={selectedSlaves}
            />
          </Box>
        </Fade>
      </Modal> */}

      {/*{editSlavesModalOpen && selectedMaster && selectedSlaves && (*/}
      {/*  <Modal*/}
      {/*    aria-labelledby="transition-modal-title"*/}
      {/*    aria-describedby="transition-modal-description"*/}
      {/*    open={editSlavesModalOpen}*/}
      {/*    onClose={handleEditSlavesClose}*/}
      {/*    closeAfterTransition*/}
      {/*    BackdropComponent={Backdrop}*/}
      {/*    BackdropProps={{*/}
      {/*      timeout: 500*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    <Fade in={editSlavesModalOpen}>*/}
      {/*      <Box sx={{ ...style, p: 0, minWidth: '90vw' }}>*/}
      {/*        <BulkEditSlaveTable*/}
      {/*          handleClose={handleEditSlavesClose}*/}
      {/*          selectedMaster={selectedMaster}*/}
      {/*          selectedSlaves={selectedSlaves}*/}
      {/*          handleSuccess={handleEditSlavesSuccess}*/}
      {/*        />*/}
      {/*      </Box>*/}
      {/*    </Fade>*/}
      {/*  </Modal>*/}
      {/*)}*/}

      {/*{selectedSlave && selectedMasterAccIDs && (*/}
      {/*  <Modal*/}
      {/*    aria-labelledby="transition-modal-title"*/}
      {/*    aria-describedby="transition-modal-description"*/}
      {/*    open={openSymbolSettingList}*/}
      {/*    onClose={() => {*/}
      {/*      setOpenSymbolSettingList(false);*/}
      {/*    }}*/}
      {/*    closeAfterTransition*/}
      {/*    BackdropComponent={Backdrop}*/}
      {/*    BackdropProps={{*/}
      {/*      timeout: 500*/}
      {/*    }}*/}
      {/*  >*/}
      {/*    <Fade in={openSymbolSettingList}>*/}
      {/*      <Box sx={{ ...style, p: 0, minWidth: '90vw' }}>*/}
      {/*        <SymbolSettingsTableSection*/}
      {/*          handleClose={() => {*/}
      {/*            setOpenSymbolSettingList(false);*/}
      {/*          }}*/}
      {/*          slave={selectedSlave}*/}
      {/*          masterAccountID={selectedMasterAccIDs.accountID}*/}
      {/*          masterActualAccountID={selectedMasterAccIDs.actualAccountID}*/}
      {/*          handleSuccess={() => {*/}
      {/*            setOpenSymbolSettingList(false);*/}
      {/*          }}*/}
      {/*        />*/}
      {/*      </Box>*/}
      {/*    </Fade>*/}
      {/*  </Modal>*/}
      {/*)}*/}
    </>
  );
};

export default CopyTradingTableSection;
